<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from './AppIcon.vue'
import { useSession } from '../composables/useSession'
import { initials } from '../utils'
import { userRoleLabel } from '../api/users'

const route = useRoute()
const router = useRouter()
const session = useSession()
const menuOpen = ref(false)

const navigation = computed(() => [
  { to: '/', label: 'Обзор', icon: 'dashboard' },
  { to: '/bookings', label: 'Бронирования', icon: 'bookings' },
  { to: '/inquiries', label: 'Заявки с сайта', icon: 'inquiries', adminOnly: true },
  { to: '/resources', label: 'Объекты', icon: 'resources' },
  { to: '/calendar', label: 'Календарь', icon: 'calendar' },
  { to: '/services', label: 'Услуги', icon: 'check', adminOnly: true },
  { to: '/products', label: 'Товары', icon: 'resources', adminOnly: true },
  { to: '/sales', label: 'Продажи', icon: 'bookings', adminOnly: true },
  { to: '/users', label: 'Пользователи', icon: 'users', adminOnly: true },
].filter((item) => !item.adminOnly || session.user?.role === 'admin'))

async function logout() {
  await session.logout()
  await router.push('/login')
}

function closeMenu() {
  menuOpen.value = false
}
</script>

<template>
  <div class="app-layout">
    <a class="skip-link" href="#main-content">К содержанию</a>

    <header class="mobile-header">
      <RouterLink to="/" class="mobile-brand" aria-label="Курочкино, обзор">
        <span class="brand-mark brand-mark--small" aria-hidden="true">
          <svg viewBox="0 0 34 34" fill="none">
            <path d="M17 3c-1 7-6 10-10 14 4 0 7-1 10-4-2 4-5 7-9 10h8v8h3v-8h8c-4-3-7-6-9-10 3 3 6 4 10 4C24 13 19 10 17 3Z" fill="currentColor" />
          </svg>
        </span>
        <span>Курочкино</span>
      </RouterLink>
      <button class="icon-button mobile-menu-button" type="button" :aria-expanded="menuOpen" aria-label="Открыть меню" @click="menuOpen = !menuOpen">
        <AppIcon :name="menuOpen ? 'close' : 'menu'" />
      </button>
    </header>

    <Transition name="fade">
      <button v-if="menuOpen" class="nav-scrim" type="button" aria-label="Закрыть меню" @click="closeMenu" />
    </Transition>

    <aside class="sidebar" :class="{ 'sidebar--open': menuOpen }">
      <RouterLink to="/" class="brand" aria-label="Курочкино, обзор" @click="closeMenu">
        <span class="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 48 48" fill="none">
            <path d="M24 3C21 13 14 18 7 24c6 0 11-2 16-7-3 7-8 11-14 15h13v12h4V32h13c-6-4-11-8-14-15 5 5 10 7 16 7C34 18 27 13 24 3Z" fill="currentColor" />
          </svg>
        </span>
        <span class="brand-copy">
          <strong>Курочкино</strong>
          <small>управление базой</small>
        </span>
      </RouterLink>

      <nav class="main-nav" aria-label="Основная навигация">
        <RouterLink
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          class="nav-link"
          :class="{ 'nav-link--active': route.path === item.to }"
          @click="closeMenu"
        >
          <AppIcon :name="item.icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-bottom">
        <div class="user-summary">
          <span class="user-avatar" aria-hidden="true">{{ initials(session.user?.displayName) }}</span>
          <span class="user-copy">
            <strong>{{ session.user?.displayName }}</strong>
            <small>{{ userRoleLabel(session.user?.role) }}</small>
          </span>
          <button class="icon-button icon-button--inverse" type="button" title="Выйти" aria-label="Выйти" @click="logout">
            <AppIcon name="logout" :size="18" />
          </button>
        </div>
      </div>
    </aside>

    <main id="main-content" class="workspace" tabindex="-1">
      <slot />
    </main>
  </div>
</template>
